import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  DONT_NOTIFY,
  NOTIFICATIONS_CTA_ICON,
  NOTIFICATIONS_CTA_PRIMARY_TRANSLATIONS,
  NOTIFICATIONS_CTA_SECONDARY_TRANSLATIONS,
  NOTIFICATIONS_CTA_TRANSLATIONS,
  NOTIFICATIONS_TRANSLATIONS,
  NotificationGroup,
  NotificationsStatus,
  NotificationType,
  NOTIFICATION_GROUP,
  NOTIFICATION_STATUS,
  NotificationBy,
  NOTIFICATION_BY,
} from 'models/Notification';
import NotificationsContext from './Notifications.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { getNotificationRedirectionPath } from './redirections';
import AuthContext from 'providers/Auth/Auth.context';
import { useLocation, useNavigate } from 'react-router-dom';
import notify from 'modules/notify';
import api from 'api';
import utils, {
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
  sentryError,
} from 'utils';
import { DEFAULT_ANIMATION_TIME_MS } from 'components/PopNotificationCard/PopNotificationCard.component';
import { getNotificationRedirectionDynamicPath } from './redirections-dynamic';
import { useTranslation } from 'react-i18next';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { getNudgesString } from './utils';
import useQueryParams from 'hooks/useQueryParams';
import useAction from './hooks/useAction';
import useLoadingState from 'hooks/useLoadingState';
import useNotificationsQuery from './hooks/useNotificationsQuery';
import useNotificationsCountQuery from './hooks/useNotificationsCountQuery';
import LeadNotificationsProvider from 'providers/LeadNotifications';
import useNotificationsCommon from 'hooks/useNotificationCommon';
import NotificationsDrawer from 'components/NotificationsDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

const Notifications: React.FC = (props) => {
  const { children } = props;

  const [notificationStatus, setNotificationStatus] =
    useState<NotificationsStatus>(NOTIFICATION_STATUS.ALL);
  const [notificationGroup, setNotificationGroup] = useState<NotificationGroup>(
    NOTIFICATION_GROUP.FYA,
  );
  const [notificationBy, setNotificationBy] = useState<NotificationBy>(
    NOTIFICATION_BY.FOR_YOU,
  );

  const { popServerError } = useContext(PopNotificationsContext);
  const { isLoggedIn } = useContext(AuthContext);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();

  const notificationsRef = useRef<NotificationType[]>([]);

  const { getIsLoading, updateIsLoading, updateIsLoadingIds } =
    useLoadingState();

  const { getAction } = useAction();

  const navigate = useNavigate();

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    params: { sessionId },
  } = useQueryParams();

  const {
    query: {
      data: notificationsData,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    },
    invalidate,
    updateNotifications,
  } = useNotificationsQuery({
    group: notificationGroup,
    status: notificationStatus,
    enabled: isLoggedIn,
  });

  const { notifications, numberOfPlaceholders, onNotificationsScroll } =
    useNotificationsCommon(
      notificationsData,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    );

  const {
    query: { data: notificationsCount },
    updateNotificationsCount,
  } = useNotificationsCountQuery({
    enabled: isLoggedIn,
  });

  const getCtaText = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_TRANSLATIONS[n?.typeCode] || 'review'),
    [t],
  );

  const getCtaPrimary = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_PRIMARY_TRANSLATIONS[n?.typeCode] || ''),
    [t],
  );

  const getCtaSecondary = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_SECONDARY_TRANSLATIONS[n?.typeCode] || ''),
    [t],
  );

  const getCtaIcon = useCallback((n: NotificationType) => {
    const Icon = NOTIFICATIONS_CTA_ICON[n?.typeCode] || ArrowrightIcon;

    return <Icon />;
  }, []);

  const getContent = useCallback(
    (notification: NotificationType) => {
      if (!notification) return '';

      const {
        typeCode,
        content: {
          sender,
          lead,
          listing,
          numberOfEnabledDocuments,
          dataRoomQuestion,
          dataRoomQuestionSubject,
          dealRoom,
          fileNames,
          firstParticipant,
          secondParticipant,
          shareableLink,
          withdrawnInterest,
        },
      } = notification || {};

      return t(NOTIFICATIONS_TRANSLATIONS[typeCode], {
        listingTitle: utils.getShortenedString(
          listing?.listingTitle ||
            dealRoom?.portfolioName ||
            dealRoom?.name ||
            listing?.dataRoomName,
        ),
        username: getUserFullNameOrEmail(sender),
        leadName: getUserFullNameOrEmail(lead || sender),
        dataRoomName: dealRoom?.name || listing?.dataRoomName,
        count: (fileNames || []).length,
        documentsNames: getJoinedWordsWithComma(fileNames),
        nudgesFileString: getNudgesString(fileNames),
        questionSubject: dataRoomQuestion?.subject || dataRoomQuestionSubject,
        documentName: dataRoomQuestion?.dataRoomNode?.name
          ? `for "${dataRoomQuestion?.dataRoomNode?.name}"`
          : '',
        numOfDocsMessage: t('numberOfDocsMessage', {
          count: numberOfEnabledDocuments || 0,
        }),
        dealName: dealRoom?.name,
        firstParticipantUsername: getUserFullNameOrEmail(firstParticipant),
        secondParticipantUsername: getUserFullNameOrEmail(secondParticipant),
        secureLinkName: shareableLink?.name,
        withdrawnText: t(
          withdrawnInterest
            ? 'theyWithdrawnInterest'
            : 'theyDidntWithdrawnInterest',
        ),
      });
    },
    [t],
  );

  const redirectionPath = useCallback(
    ({
      typeCode,
      userType,
      content: {
        listing,
        sessionId,
        envelopeTemplateUrl,
        dataRoomQuestion,
        leadId,
        dataRoomNode,
        shareableLink,
      },
    }: NotificationType) =>
      getNotificationRedirectionPath({
        typeCode,
        listing,
        sessionId,
        envelopeTemplateUrl,
        leadId,
        type: userType,
        question: dataRoomQuestion,
        dataRoomNodeId: dataRoomNode?._id,
        shareableLink,
      }),
    [],
  );

  const getDynamicRedirectionPath = useCallback(
    (notif: NotificationType) => getNotificationRedirectionDynamicPath(notif),
    [],
  );

  const readAllNotifications = useCallback(async () => {
    try {
      updateIsLoading(true);

      await api.notifications.readAllNotifications();

      updateNotificationsCount((old) => ({ ...old, unread: 0 }));
      updateNotifications((items) =>
        items.map((notif) => ({ ...notif, isRead: true })),
      );

      invalidate();
    } catch (e) {
      sentryError(e);
      popServerError(e);
    } finally {
      updateIsLoading(false);
    }
  }, [
    updateIsLoading,
    updateNotificationsCount,
    updateNotifications,
    invalidate,
    popServerError,
  ]);

  const deleteNotification = useCallback(
    async (notification: NotificationType) => {
      try {
        updateIsLoadingIds(notification._id);

        const { id: notifId, isRead } = notification;
        await api.notifications.deleteNotification(notifId);

        updateNotificationsCount((old) => ({
          ...old,
          all: old.all - 1,
          unread: !isRead ? old.unread - 1 : old.unread,
        }));

        updateNotifications((items) =>
          items.filter((notif) => notif.id !== notifId),
        );

        invalidate();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        updateIsLoadingIds(notification._id, false);
      }
    },
    [
      updateIsLoadingIds,
      updateNotificationsCount,
      updateNotifications,
      invalidate,
      popServerError,
    ],
  );

  const onDeleteNotification = useCallback(
    (notification: NotificationType) => {
      deleteNotification(notification);
    },
    [deleteNotification],
  );

  const updateIsRead = useCallback(
    (notifId: string, isRead: boolean) => {
      updateNotificationsCount((old) => ({
        ...old,
        unread: isRead ? (old.unread > 0 ? old.unread - 1 : 0) : old.unread + 1,
      }));

      updateNotifications((items) =>
        items.map((notif) => {
          if (notif._id !== notifId) return notif;
          return { ...notif, isRead };
        }),
      );

      invalidate();
    },
    [updateNotificationsCount, updateNotifications, invalidate],
  );

  const readNotification = useCallback(
    async (notifId: string) => {
      const resolvedNotification = notificationsRef.current.find(
        ({ _id }) => _id === notifId,
      );

      const { isRead } = resolvedNotification || {};

      try {
        // read notification by id and update values for
        // count of unread notifications
        updateIsLoadingIds(notifId);

        if (isRead) return;

        await api.notifications.readNotification(notifId);

        updateIsRead(notifId, true);
      } catch (e) {
        sentryError(e);
      } finally {
        updateIsLoadingIds(notifId, false);
      }
    },
    [updateIsLoadingIds, updateIsRead],
  );

  const unreadNotification = useCallback(
    async (notifId: string) => {
      const resolvedNotification = notificationsRef.current.find(
        ({ _id }) => _id === notifId,
      );

      const { isRead } = resolvedNotification || {};

      try {
        // unread notification by id and update values for
        // count of unread notifications
        updateIsLoadingIds(notifId);

        if (!isRead) return;

        await api.notifications.unreadNotification(notifId);

        updateIsRead(notifId, false);
      } catch (e) {
        sentryError(e);
      } finally {
        updateIsLoadingIds(notifId, false);
      }
    },
    [updateIsLoadingIds, updateIsRead],
  );

  const onKeepDrawerOpen = useCallback(
    (keepDrawerOpen: boolean) =>
      drawer?.setOpen((alreadyOpened) => alreadyOpened && keepDrawerOpen),
    [drawer],
  );

  const onReadNotification = useCallback(
    (notifId: string, keepDrawerOpen = false) => {
      readNotification(notifId);
      onKeepDrawerOpen(keepDrawerOpen);
    },
    [onKeepDrawerOpen, readNotification],
  );

  const onUnreadNotification = useCallback(
    (notifId: string, keepDrawerOpen = false) => {
      unreadNotification(notifId);
      onKeepDrawerOpen(keepDrawerOpen);
    },
    [onKeepDrawerOpen, unreadNotification],
  );

  const onDynamicPath = useCallback(
    (notification: NotificationType) =>
      getNotificationRedirectionDynamicPath(notification),
    [],
  );

  const onStaticPath = useCallback((notification: NotificationType) => {
    const {
      typeCode,
      userType,
      content: {
        listing,
        sessionId,
        envelopeTemplateUrl,
        dataRoomQuestion,
        leadId,
        dataRoomNode,
        shareableLink,
      },
    } = notification;

    const pathname = getNotificationRedirectionPath({
      typeCode,
      listing,
      sessionId,
      envelopeTemplateUrl,
      leadId,
      type: userType,
      question: dataRoomQuestion,
      dataRoomNodeId: dataRoomNode?._id,
      shareableLink,
    });
    const isExternalPathname = !pathname || pathname.startsWith('http');

    if (isExternalPathname) return;

    return pathname;
  }, []);

  const onCtaClick = useCallback(
    async (notification: NotificationType) => {
      const staticPath = onStaticPath(notification);

      const action = getAction(notification);

      if (action) {
        action({ notification });
        return;
      }

      if (staticPath) {
        navigate(staticPath);
        return;
      }

      try {
        const externalUrl = await onDynamicPath(notification)();

        if (externalUrl) {
          window.location.href = externalUrl;
        }
      } catch (e) {
        popServerError(e);
        throw e;
      }
    },
    [getAction, navigate, onDynamicPath, onStaticPath, popServerError],
  );

  const notificationCreated = useCallback(
    (notification: NotificationType) => {
      if (!notification.justNotify) {
        // Invalidate all notification queries so they refetch in the background
        invalidate();

        // Update count cache optimistically
        updateNotificationsCount((old) => ({
          all: old.all + 1,
          unread: old.unread + 1,
        }));
      }

      const dontShowMessageNotification =
        (sessionId && sessionId === notification.content.sessionId) ||
        (notification.content.sessionId &&
          pathname.includes(notification.content.sessionId));

      const dontNotify = DONT_NOTIFY.includes(notification.typeCode);

      if (!dontNotify && !dontShowMessageNotification) {
        notify({
          notification,
          ctaText: getCtaText(notification),
          ctaIcon: getCtaIcon(notification),
          contentText: getContent(notification),
          dissappearAfter: DEFAULT_ANIMATION_TIME_MS,
          onCtaClick: () => onCtaClick(notification),
          onClick: () => readNotification(notification._id),
        });
      }
    },
    [
      sessionId,
      pathname,
      invalidate,
      updateNotificationsCount,
      getCtaText,
      getCtaIcon,
      getContent,
      onCtaClick,
      readNotification,
    ],
  );

  useSocketEvent(LISTEN_EVENTS.NOTIFICATION_CREATED, notificationCreated);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  return (
    <NotificationsContext.Provider
      value={{
        isDrawerOpen: drawer?.open,
        fetchingNotifications: isFetching,
        numberOfPlaceholders,
        notificationsCount,
        notifications,
        notificationStatus,
        notificationGroup,
        notificationBy,
        openDrawer: () => drawer?.setOpen(true),
        closeDrawer: () => drawer?.setOpen(false),
        setNotificationStatus,
        setNotificationGroup,
        setNotificationBy,
        deleteNotification,
        onNotificationsScroll,
        readAllNotifications,
        redirectionPath,
        getDynamicRedirectionPath,
        onDeleteNotification,
        onReadNotification,
        onUnreadNotification,
        getCtaText,
        getCtaPrimary,
        getCtaSecondary,
        getCtaIcon,
        getContent,
        getIsLoadingNotification: getIsLoading,
      }}
    >
      <LeadNotificationsProvider>
        {children}
        <NotificationsDrawer ref={drawerRef} />
      </LeadNotificationsProvider>
    </NotificationsContext.Provider>
  );
};

export default Notifications;
